import { useState } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { cn } from '@/lib/utils';
import {
  MousePointer2,
  MapPin,
  Edit3,
  Check,
  X,
  PenTool,
  ChevronUp,
  Spline,
  Pentagon,
  Type,
} from 'lucide-react';
import { LayerDefinition } from '@packages/database/types';
import { useSelectionStore } from '@/stores/selectionStore';

const getGeomIcon = (geomType: string | null | undefined) => {
  const t = geomType?.toUpperCase() ?? '';
  if (t.includes('POLYGON')) return Pentagon;
  if (t.includes('LINESTRING')) return Spline;
  return MapPin;
};

type DefaultToolKey = 'POINT' | 'LINESTRING' | 'POLYGON' | 'LABEL';

interface DefaultTool {
  key: DefaultToolKey;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  geomType: string;
}

const DEFAULT_TOOLS: DefaultTool[] = [
  { key: 'POINT', label: 'Point', icon: MapPin, geomType: 'Point' },
  { key: 'LINESTRING', label: 'Line', icon: Spline, geomType: 'LineString' },
  { key: 'POLYGON', label: 'Polygon', icon: Pentagon, geomType: 'Polygon' },
  { key: 'LABEL', label: 'Label', icon: Type, geomType: 'Point' },
];

const isDefaultTool = (tool: string): tool is DefaultToolKey =>
  ['POINT', 'LINESTRING', 'POLYGON', 'LABEL'].includes(tool);

const DefaultToolButton = ({
  tool,
  isActive,
  onClick,
}: {
  tool: DefaultTool;
  isActive: boolean;
  onClick: () => void;
}) => {
  const Icon = tool.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-start gap-2',
        'w-full h-auto min-h-[64px] p-2',
        'rounded-md transition-all border shadow-sm',
        isActive
          ? 'bg-zinc-600/80 border-zinc-400/40 text-white shadow-md'
          : 'bg-zinc-800/80 text-zinc-400 border-transparent hover:bg-zinc-700 hover:text-zinc-200',
      )}
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <span className="block w-full text-xs text-center whitespace-normal break-words leading-tight">
        {tool.label}
      </span>
      {isActive && <div className="w-1.5 h-1.5 rounded-full bg-white/70" />}
    </button>
  );
};

const LayerToolButton = ({
  layer,
  isActive,
  onClick,
}: {
  layer: LayerDefinition;
  isActive: boolean;
  onClick: () => void;
}) => {
  const color = layer.styles?.color ?? '#3b82f6';
  const Icon = getGeomIcon(layer.geomType);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-start gap-2',
        'w-full h-auto min-h-[64px] p-2',
        'rounded-md transition-all border shadow-sm',
        isActive
          ? 'bg-slate-700/80 border-white/20 shadow-md'
          : 'bg-slate-800/80 text-slate-300 border-transparent hover:bg-slate-700',
      )}
      style={isActive ? { borderColor: `${color}80` } : undefined}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{
          backgroundColor: `${color}25`,
          border: `2px solid ${color}`,
        }}
      >
        <Icon size={14} style={{ color }} />
      </div>
      <span className="block w-full text-xs text-center text-slate-300 whitespace-normal break-words leading-tight">
        {layer.displayName}
      </span>
      {isActive && (
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
    </button>
  );
};

// ─── Mode display config (text + icon only, no behavior) ────────────────────

interface ModeDisplay {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
  isReady: boolean;
}

export const MapInstructionOverlay = () => {
  const [paletteOpen, setPaletteOpen] = useState(false);

  const {
    interactionMode,
    completeInteraction,
    cancelInteraction,
    markerLocation,
    activeRedlineTool,
    setRedlineTool,
    redlineData,
    availableRedlineTools,
    layers,
  } = useMapStore();

  const { projectArea } = useSelectionStore();

  if (interactionMode === 'view') return null;

  // =========================================================================
  // REDLINING MODE — full tool palette UI
  // =========================================================================

  if (interactionMode === 'redlining') {
    const featureCount = redlineData?.features.length ?? 0;

    const toolLayers: LayerDefinition[] = availableRedlineTools
      .map((slug) =>
        layers.find(
          (l) =>
            l.sourceTable === 'assets' &&
            (l.sourceFilter as any)?.assetType === slug,
        ),
      )
      .filter(Boolean) as LayerDefinition[];

    const activeLayer =
      layers.find(
        (l) =>
          l.sourceTable === 'assets' &&
          (l.sourceFilter as any)?.assetType === activeRedlineTool,
      ) ?? null;

    const ActiveToolIndicator = ({
      activeRedlineTool,
      activeLayer,
    }: {
      activeRedlineTool: string;
      activeLayer: LayerDefinition | null;
    }) => {
      if (isDefaultTool(activeRedlineTool)) {
        const defaultTool = DEFAULT_TOOLS.find(
          (t) => t.key === activeRedlineTool,
        )!;
        const Icon = defaultTool.icon;
        return (
          <>
            <Icon size={13} className="shrink-0 text-zinc-300" />
            {defaultTool.label}
          </>
        );
      }

      if (activeLayer) {
        const Icon = getGeomIcon(activeLayer.geomType);
        const color = activeLayer.styles?.color ?? '#3b82f6';
        return (
          <>
            <Icon size={13} className="shrink-0" style={{ color }} />
            {activeLayer.displayName}
          </>
        );
      }

      return (
        <>
          <PenTool size={13} className="text-zinc-400" />
          Select tool
        </>
      );
    };

    return (
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3 w-full max-w-lg px-4 pointer-events-none">
        {/* TOOL PALETTE */}
        {paletteOpen && (
          <div className="bg-zinc-900/95 dark:bg-zinc-950/95 backdrop-blur-md p-3 rounded-xl border border-white/10 shadow-2xl pointer-events-auto animate-in slide-in-from-bottom-2 fade-in duration-150 mb-2 max-h-[380px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent w-full">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold px-1 mb-2">
              Generic
            </p>
            <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(84px,1fr))] mb-3">
              {DEFAULT_TOOLS.map((tool) => (
                <DefaultToolButton
                  key={tool.key}
                  tool={tool}
                  isActive={activeRedlineTool === tool.key}
                  onClick={() => {
                    setRedlineTool(tool.key);
                    setPaletteOpen(false);
                  }}
                />
              ))}
            </div>

            {toolLayers.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">
                  Asset Types
                </span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            )}

            {toolLayers.length > 0 && (
              <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(84px,1fr))]">
                {toolLayers.map((layer) => {
                  const slug = (layer.sourceFilter as any)?.assetType;
                  const isActive = activeRedlineTool === slug;
                  return (
                    <LayerToolButton
                      key={layer.id}
                      layer={layer}
                      isActive={isActive}
                      onClick={() => {
                        setRedlineTool(slug);
                        setPaletteOpen(false);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ACTION BAR */}
        <div className="pointer-events-auto bg-zinc-900/95 dark:bg-zinc-950/95 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-xl flex items-center gap-3 w-full">
          <button
            type="button"
            onClick={() => setPaletteOpen((p) => !p)}
            className={cn(
              'flex items-center gap-2 h-8 px-3 rounded-full text-xs font-medium transition-all',
              activeRedlineTool !== 'NONE'
                ? 'text-white'
                : 'text-zinc-400 hover:text-zinc-200',
            )}
            style={
              activeLayer
                ? {
                    backgroundColor: `${activeLayer.styles?.color ?? '#3b82f6'}30`,
                    borderWidth: 1,
                    borderColor: `${activeLayer.styles?.color ?? '#3b82f6'}60`,
                  }
                : activeRedlineTool !== 'NONE'
                  ? {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      borderWidth: 1,
                      borderColor: 'rgba(255,255,255,0.15)',
                    }
                  : undefined
            }
          >
            <ActiveToolIndicator
              activeRedlineTool={activeRedlineTool}
              activeLayer={activeLayer}
            />
            <ChevronUp
              size={13}
              className={cn(
                'transition-transform opacity-60',
                paletteOpen && 'rotate-180',
              )}
            />
          </button>

          <div className="h-4 w-px bg-white/10 mx-1" />

          <span className="text-xs text-zinc-500 tabular-nums min-w-[4rem]">
            {featureCount} {featureCount === 1 ? 'object' : 'objects'}
          </span>

          <div className="h-4 w-px bg-white/10 mx-1" />

          <button
            type="button"
            onClick={cancelInteraction}
            className="text-zinc-400 hover:text-white hover:bg-white/10 h-8 px-3 text-xs rounded-full transition-all"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={completeInteraction}
            disabled={featureCount === 0}
            className={cn(
              'h-8 px-4 text-xs font-medium rounded-full transition-all text-white',
              featureCount > 0
                ? 'bg-green-600 hover:bg-green-500'
                : 'bg-zinc-700 cursor-not-allowed text-zinc-500',
            )}
          >
            Finish
          </button>
        </div>
      </div>
    );
  }

  // =========================================================================
  // OTHER MODES — simple instruction bar
  // =========================================================================

  const display: ModeDisplay = (() => {
    switch (interactionMode) {
      case 'selectProjectArea':
        return {
          text: projectArea
            ? 'Service area selected.'
            : 'Click a service area on the map.',
          icon: MapPin,
          isReady: !!projectArea,
        };
      case 'addNote':
        return {
          text: markerLocation ? 'Location set.' : 'Click map to place note.',
          icon: Edit3,
          isReady: !!markerLocation,
        };
      case 'addFieldCorrection':
        return {
          text: redlineData
            ? 'New location captured.'
            : 'Click to set new asset location.',
          icon: MapPin,
          isReady: !!redlineData,
        };
      default:
        return {
          text: '',
          icon: MousePointer2,
          isReady: false,
        };
    }
  })();

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md pointer-events-auto">
      <div className="bg-zinc-900/95 dark:bg-zinc-950/95 backdrop-blur-md border border-white/10 rounded-full shadow-xl px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className={cn(
              'p-1.5 rounded-full shrink-0',
              display.isReady
                ? 'bg-green-500/20 text-green-400'
                : 'bg-blue-500/20 text-blue-400',
            )}
          >
            <display.icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-sm text-zinc-200 font-medium truncate">
            {display.text}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={completeInteraction}
            disabled={!display.isReady}
            className={cn(
              'h-7 px-3 text-xs font-medium rounded-full transition-all',
              display.isReady
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'bg-white/5 text-zinc-600 cursor-not-allowed',
            )}
          >
            Done
          </button>
          <button
            type="button"
            onClick={cancelInteraction}
            className="h-7 w-7 flex items-center justify-center rounded-full text-zinc-500 hover:text-zinc-200 hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
